import { Copy } from "lucide-react";
import React from "react";

import { Modal } from "../ui/modal";

const JOB_POSTING_EMAIL = "promotejobs@dproz.com";

type CompanyJobPostInstructionsModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

function CompanyJobPostInstructionsModal({ isOpen, onClose }: CompanyJobPostInstructionsModalProps) {
	const [copyState, setCopyState] = React.useState<"idle" | "copied" | "error">("idle");

	React.useEffect(() => {
		if (!isOpen) {
			setCopyState("idle");
		}
	}, [isOpen]);

	React.useEffect(() => {
		if (copyState !== "copied") return;

		const timeoutId = setTimeout(() => {
			setCopyState("idle");
		}, 1500);

		return () => clearTimeout(timeoutId);
	}, [copyState]);

	const handleCopyEmail = async () => {
		try {
			if (navigator?.clipboard?.writeText) {
				await navigator.clipboard.writeText(JOB_POSTING_EMAIL);
			} else {
				const tempInput = document.createElement("input");
				tempInput.value = JOB_POSTING_EMAIL;
				document.body.appendChild(tempInput);
				tempInput.select();
				document.execCommand("copy");
				document.body.removeChild(tempInput);
			}

			setCopyState("copied");
		} catch {
			setCopyState("error");
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="How to post jobs on Dproz"
			description="Follow these instructions to submit your company job openings."
			className="max-w-xl"
			titleClassName=""
		>
			<div className="px-6 py-5 space-y-4 font-inter">
				<div className="text-[14px] text-gray-700 leading-relaxed">
					To post jobs, send the details to{" "}
					<span className="inline-flex items-center gap-1.5 align-middle">
						<a
							href={`mailto:${JOB_POSTING_EMAIL}`}
							className="font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2 break-all"
						>
							{JOB_POSTING_EMAIL}
						</a>
						<button
							type="button"
							onClick={handleCopyEmail}
							aria-label="Copy email address"
							className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
						>
							<Copy className="h-3.5 w-3.5" />
						</button>
					</span>
					.
					{copyState !== "idle" && (
						<span className="ml-2 text-xs text-gray-500">{copyState === "copied" ? "Copied" : "Could not copy"}</span>
					)}
				</div>

				<ul className="space-y-2.5 text-[14px] text-gray-700 list-disc pl-5 leading-relaxed">
					<li>Send the request from your company email domain, not free providers like Gmail.</li>
					<li>
						Include the job descriptions for all jobs you want to post, either in the email body or as attachments.
					</li>
				</ul>
			</div>
		</Modal>
	);
}

export default CompanyJobPostInstructionsModal;
